import type { MarketExceptionType } from '@/constants/MarketExceptions/MarketExceptionTypes'
import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { isAvailableForCustomerType } from '@/lib/utils/user/isAvailableForCustomerType'
import type { CustomerType } from '@/store/types/user'
import useGlobalStore from '@/store/useGlobalStore'
import { useRouter } from 'next/router'
import { ProductsGridCard } from './ProductsGridCard'

const getSortedProducts = ({
  products,
  customerType,
  sortBy = 'name',
  orderBy = 'asc',
  marketExceptions,
}: {
  products: CosmicProductType[] | undefined | null
  customerType: CustomerType
  sortBy: string
  orderBy: string
  marketExceptions: MarketExceptionType | null
}) => {
  if (!products) return []

  const productsSorted = [...products].sort((a, b) => {
    let productsSortedReturn = 0

    const isInventoryData = sortBy === 'price' || sortBy === 'pv'

    if (isInventoryData) {
      const priceOrPv = sortBy === 'price' ? 'priceEach' : 'pvEach'

      const aPrice =
        a.variations?.[0]?.pricing?.levels.find(
          (level) => level.customer === customerType,
        )?.terms[priceOrPv] || 0

      const bPrice =
        b.variations?.[0]?.pricing?.levels.find(
          (level) => level.customer === customerType,
        )?.terms[priceOrPv] || 0

      productsSortedReturn =
        orderBy === 'asc' ? aPrice - bPrice : bPrice - aPrice
    } else if (sortBy === 'name') {
      const sortByKey = 'name'

      const aSortBy = a[sortByKey]?.toLowerCase() ?? ''
      const bSortBy = b[sortByKey]?.toLowerCase() ?? ''

      productsSortedReturn =
        orderBy === 'asc'
          ? aSortBy.localeCompare(bSortBy)
          : bSortBy.localeCompare(aSortBy)
    }

    return productsSortedReturn
  })

  if (sortBy === 'name' && marketExceptions?.productOrder) {
    const { productOrder } = marketExceptions
    const firstProducts = productOrder.first || []
    const lastProducts = productOrder.last || []

    const productMap = new Map(productsSorted.map((p) => [p.slug, p]))

    const firstOrderedProducts: CosmicProductType[] = []
    for (const slug of firstProducts) {
      const product = productMap.get(slug)
      if (product) {
        firstOrderedProducts.push(product)
        productMap.delete(slug)
      }
    }

    const lastOrderedProducts: CosmicProductType[] = []
    for (const slug of lastProducts) {
      const product = productMap.get(slug)
      if (product) {
        lastOrderedProducts.push(product)
        productMap.delete(slug)
      }
    }

    const remainingProducts = [...productMap.values()]

    return [
      ...firstOrderedProducts,
      ...remainingProducts,
      ...lastOrderedProducts,
    ]
  }

  return productsSorted
}

type Props = {
  products: CosmicProductType[] | null
}

export const ProductsGrid = ({ products }: Props) => {
  const {
    user: { customerType },
    app: { marketExceptions },
  } = useGlobalStore()
  const router = useRouter()
  const { sortBy = 'name', orderBy = 'asc' } = router.query

  const sortedProducts = getSortedProducts({
    products,
    customerType,
    sortBy: sortBy as string,
    orderBy: orderBy as string,
    marketExceptions,
  })

  return (
    <div className="mb-10 grid w-full grid-cols-2 gap-2 px-2 sm:gap-4 sm:px-5 md:grid-cols-4">
      {sortedProducts?.map((product, index) => {
        if (
          !isAvailableForCustomerType({
            customerType,
            availableFor: product.availableFor,
          })
        ) {
          return null
        }

        return (
          <ProductsGridCard
            product={product}
            cardIndex={index}
            key={product.name + product.slug}
          />
        )
      })}
    </div>
  )
}
